import { Controller } from "@hotwired/stimulus"
import SlimSelect from "slim-select"
import { matchesCompanyName } from "../../utils/text-normalize.util"

// Connects to data-controller="invoices--select-contact"
export default class extends Controller {
  static targets = ["select", "selectContactGroup"]

  static values = {
    searchPlaceholder: {
      type: String,
      default: "Search contact..."
    },
    searchText: {
      type: String,
      default: "Contact not found"
    },
    isContactSelected: {
      type: Boolean,
      default: false
    }
  }

  connect() {
    this.isOptionClicked = false
    this.#hideBlankOption()

    this._changeContactHandler = (_event) => {
      this.selectContactGroupTarget.classList.remove("d-none")
      this._openTimer = setTimeout(() => {
        this.selectInstance.open()
      }, 250)
    }
    this._modalRemovedHandler = (_event) => {
      this.#updateSlimSelect()
    }

    this.selectInstance = new SlimSelect({
      select: this.selectTarget,
      value: this.selectTarget.value,
      settings: {
        searchPlaceholder: this.searchPlaceholderValue,
        searchText: this.searchTextValue,
        contentLocation: document.getElementById("select-contact__content"),
        contentPosition: "relative",
        // slim-select 4 defaults to a modal on phones; the list is laid out inside its own wrapper here
        modal: "off"
      },
      events: {
        // SlimSelect's default filter is a raw substring match, so "R+S service"
        // misses a contact stored as "R + S service". Mirror the server's
        // tolerant company-name matching instead (LUC-705).
        searchFilter: (option, search) => matchesCompanyName(option.text, search),
        beforeOpen: () => {
          this.isOptionClicked = false
          this.#onClickOption()
        },
        afterChange: (newVal) => {
          this.#dispatchSelectedContactEvent(newVal[0])
          if (this.#isValidSelectedContact(newVal[0])) {
            this.selectContactGroupTarget.classList.add("d-none")
          } else {
            this.selectContactGroupTarget.classList.remove("d-none")
          }
        },
        beforeClose: () => {
          // SlimSelect debounces this by 100ms and destroy() does not cancel the
          // timer, so it can still fire after disconnect() disposed the instance.
          if (!this.selectInstance) return

          //situation when there is only one option in the select
          if (!this.isOptionClicked) {
            const selected = this.selectInstance.getData().find((item) => item.selected && item.value !== "")

            this.isContactSelectedValue = selected !== undefined
            if (selected) {
              this.selectContactGroupTarget.classList.add("d-none")
              this.#dispatchSelectedContactEvent(selected)
            }
          }
        }
      }
    })

    document.addEventListener("changeContact", this._changeContactHandler)
    document.addEventListener("modalRemoved", this._modalRemovedHandler)

    this.selectTarget.classList.remove("d-none")

    // change input type to text to hide native clear button
    const searchInputEl = this.element.querySelector(".ss-search input")
    searchInputEl.setAttribute("type", "text")

    searchInputEl.addEventListener("input", (event) => {
      this.#onSearchInput(event.target)
    })
  }

  disconnect() {
    document.removeEventListener("changeContact", this._changeContactHandler)
    document.removeEventListener("modalRemoved", this._modalRemovedHandler)

    clearTimeout(this._openTimer)

    // SlimSelect registers window resize/scroll and document visibilitychange
    // listeners of its own and only releases them in destroy(). Without this
    // every Turbo navigation away from an invoice form leaks a full set.
    this.selectInstance?.destroy()
    this.selectInstance = null
  }

  setData(data) {
    this.selectInstance.setData(data)
  }

  open(event) {
    event.stopPropagation()
    this.selectInstance.open()
  }

  clearSearch(_event) {
    // clear search input
    const searchInput = this.element.querySelector(".ss-search input")

    searchInput.value = ""

    // Dispatch an 'input' event to simulate native behavior
    const inputEvent = new Event("input", { bubbles: true })
    searchInput.dispatchEvent(inputEvent)

    searchInput.focus()
  }

  #dispatchSelectedContactEvent(contact) {
    // Try to get country_id from the SlimSelect data first, then fall back to original HTML option
    let countryId = contact.data.countryId || contact.country_id || null

    // Fallback to original HTML option if not found in SlimSelect data
    if (!countryId) {
      const originalOption = Array.from(this.selectTarget.options).find(
        (option) => option.value === String(contact.value)
      )
      countryId = originalOption?.dataset?.countryId || null
    }

    // Same two-source read as countryId: SlimSelect's own data first, the
    // original <option> second — a contact added through the modal reaches the
    // instance as raw options, without the data object.
    let email = contact.data.email || contact.email || null
    if (!email) {
      const originalOption = Array.from(this.selectTarget.options).find(
        (option) => option.value === String(contact.value)
      )
      email = originalOption?.dataset?.email || null
    }

    const event = new CustomEvent("selectedContact", {
      detail: {
        text: contact.text,
        value: contact.value,
        country_id: countryId,
        email: email
      }
    })
    document.dispatchEvent(event)
  }

  #isValidSelectedContact(contact) {
    return contact.text !== "" && contact.value !== ""
  }

  #updateSlimSelect() {
    this.selectInstance.setData(this.selectTarget.options)
    this.#hideBlankOption()
  }

  #hideBlankOption() {
    const blankOption = this.selectTarget.querySelector('option[value=""]')
    if (blankOption) {
      blankOption.style.display = "none"
    }
  }

  #onSearchInput(searchInputEl) {
    const searchValue = searchInputEl.value

    if (searchValue.length > 0) {
      this.element.querySelector(".select-contact__clear-icon").classList.remove("d-none")
    } else {
      this.element.querySelector(".select-contact__clear-icon").classList.add("d-none")
    }
  }

  // Close select contact dropdown when clicking on any option
  // hack: It was not possible to select selected option
  #onClickOption() {
    const options = this.element.querySelectorAll(".ss-option")
    options.forEach((option) => {
      option.addEventListener("click", () => {
        this.selectInstance.close()
        this.isOptionClicked = true
      })
    })
  }
}
